import React, { useEffect, useState, useRef, useCallback, useMemo, type ReactNode } from "react";
import NameFilterBar from '../../components/NameFilterBar';
import { nachNamenFiltern } from '../../utilities/namensfilter';

import { userAPI } from "../../UserAPI";
import { useAtom } from "jotai";
import { authenticationAtom } from "../../State";
import { MarketplaceOffer, AgenticWorkflow, OfferStatus } from "@the-real-insight/tri-model";
import { useModalContext } from "../../components/ModalProvider";
import { useTranslation } from "react-i18next";
import { useTestDialog } from "../hooks/useTestDialog";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useUrlPageState } from "../../utilities/dataTablePagination";
import truncate from "lodash.truncate";
import OfferStatusManagement, { InternallyAvailableCheckbox } from '../OfferStatusManagement';
import { withMarketplaceMenuItems } from '../marketplacePublishing';
import MarketingDetails from '../MarketingDetails';
import SampleRequests from '../SampleRequests';
import PlanPanel from '../PlanPanel';
import ZodEditor from '../prompt-tool/ZodEditor';
import { parseZodSchema, extractFieldsFromZodSchema } from '../../utilities/jsonValidation';
import { FluentIconSelector } from "../../components/FluentIconSelector";
import { ColorSelector } from "../../components/ColorSelector";
import BpmnWorkflowEditor from './BpmnWorkflowEditor';
import { ToolUsageList } from "../components/ToolUsageList";
import { OfferRowMenu, YesNoBadge, rowMenuColumnMeta } from "../components/offerTable";
import { MarketplaceOfferNameCell } from "../components/MarketplaceOfferNameCell";
import ToolConfigurationForm from '../ToolConfigurationForm';
import type { ConfigurationParameter } from '../ConfigurationSchemaEditor';

import type { Column, ColumnDef } from '@tanstack/react-table';
import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Plus,
    Rocket,
    Trash2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/** Klickbarer Spaltenkopf — ersetzt das `sortable`-Flag der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                : sorted === 'desc'
                    ? <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden />}
        </Button>
    );
}

/** Spaltenkopf ohne Sortierung — gleiche Schriftstärke/Farbe wie die sortierbaren Köpfe. */
const plainHeader = (text: string) => () => <span className="font-bold text-foreground">{text}</span>;

// ZodField interface matching the one from tri-model
interface _ZodField {
    id: string;
    name: string;
    type: 'string' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'file' | 'array' | 'object';
    required: boolean;
    description: string;
    defaultValue: string;
    children: _ZodField[];
    arrayType?: 'string' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'file' | 'object';
}

const GRAPH_CONNECTOR_SCHEMA: ConfigurationParameter[] = [
    { id: 'tenantId', name: 'Azure AD Tenant ID', type: 'string' },
    { id: 'clientId', name: 'Azure AD Client ID', type: 'string' },
    { id: 'clientSecret', name: 'Azure AD Client Secret', type: 'password' },
];

// Per-tenant resource targets for graph-mailbox: which inbox to watch.
// Shown alongside the shared Graph credentials when the workflow uses a
// graph-mailbox start trigger.
const GRAPH_MAILBOX_RESOURCE_SCHEMA: ConfigurationParameter[] = [
    { id: 'mailbox', name: 'Mailbox address', type: 'string' },
];

// Per-tenant resource targets for sharepoint-folder: which site/drive/folder
// to watch. Shown when the workflow uses a sharepoint-folder start trigger.
const SHAREPOINT_FOLDER_RESOURCE_SCHEMA: ConfigurationParameter[] = [
    { id: 'siteUrl', name: 'SharePoint site URL', type: 'string' },
    { id: 'driveName', name: 'Document library (drive name)', type: 'string' },
    { id: 'folderPath', name: 'Folder path', type: 'string' },
];

const AI_LISTENER_CONNECTOR_SCHEMA: ConfigurationParameter[] = [
    { id: 'aiListenerApiKey', name: 'AI Listener API Key', type: 'password' },
    { id: 'aiListenerModel', name: 'AI Listener Model', type: 'string' },
];

// ZodField type is now imported from @the-real-insight/tri-model

// Helper function to extract fields from input schema, handling both old and new formats
const getInputSchemaFields = (workflow: any): any[] => {
    console.log('🔍 getInputSchemaFields called for workflow:', workflow?.name);
    console.log('🔍 inputSchema:', workflow?.inputSchema);
    
    if (!workflow || !workflow.inputSchema) {
        console.log('🔍 No inputSchema found, returning empty array');
        return [];
    }
    
    // If inputSchema is a string (old format), try to parse it
    if (typeof workflow.inputSchema === 'string') {
        console.log('🔍 Input schema is string, parsing...');
        const zodSchema = parseZodSchema(workflow.inputSchema);
        if (zodSchema) {
            const fields = extractFieldsFromZodSchema(zodSchema);
            console.log('🔍 Extracted input fields from string:', fields);
            return fields;
        }
    }
    
    // If inputSchema is already an array (old format), return it directly
    if (Array.isArray(workflow.inputSchema)) {
        console.log('🔍 Input schema is array, returning directly:', workflow.inputSchema);
        return workflow.inputSchema;
    }
    
    // If inputSchema is a JSON Schema object (new format), unmarshal it and extract fields
    if (typeof workflow.inputSchema === 'object' && workflow.inputSchema.type === 'object') {
        try {
            // For now, we'll need to handle this case differently
            // The JSON Schema format doesn't contain enough information to extract file types
            // We'll need to rely on the backend to provide the correct format
            console.log('JSON Schema format detected for input, but file type extraction not yet implemented');
            return [];
        } catch (error) {
            console.error('Error extracting fields from input JSON Schema:', error);
            return [];
        }
    }
    
    return [];
};

// Helper function to extract fields from output schema, handling both old and new formats
const getOutputSchemaFields = (workflow: any): any[] => {
    console.log('🔍 getOutputSchemaFields called for workflow:', workflow?.name);
    console.log('🔍 outputSchema:', workflow?.outputSchema);
    
    if (!workflow || !workflow.outputSchema) {
        console.log('🔍 No outputSchema found, returning empty array');
        return [];
    }
    
    // If outputSchema is a string (old format), try to parse it
    if (typeof workflow.outputSchema === 'string') {
        console.log('🔍 Output schema is string, parsing...');
        const zodSchema = parseZodSchema(workflow.outputSchema);
        if (zodSchema) {
            const fields = extractFieldsFromZodSchema(zodSchema);
            console.log('🔍 Extracted output fields from string:', fields);
            return fields;
        }
    }
    
    // If outputSchema is already an array (old format), return it directly
    if (Array.isArray(workflow.outputSchema)) {
        console.log('🔍 Output schema is array, returning directly:', workflow.outputSchema);
        console.log('🔍 Array contents:', JSON.stringify(workflow.outputSchema, null, 2));
        return workflow.outputSchema;
    }
    
    // If outputSchema is a JSON Schema object (new format), unmarshal it and extract fields
    if (typeof workflow.outputSchema === 'object' && workflow.outputSchema.type === 'object') {
        try {
            // For now, we'll need to handle this case differently
            // The JSON Schema format doesn't contain enough information to extract file types
            // We'll need to rely on the backend to provide the correct format
            console.log('JSON Schema format detected for output, but file type extraction not yet implemented');
            return [];
        } catch (error) {
            console.error('Error extracting fields from output JSON Schema:', error);
            return [];
        }
    }
    
    return [];
};

export default function AgenticWorkflowManagement() {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showDeleteDialog } = useModalContext() as any;
    const { TestDialog, openTestDialog, closeTestDialog: _closeTestDialog } = useTestDialog();
    const [searchParams, setSearchParams] = useSearchParams();
    const paginationProps = useUrlPageState('agenticWorkflowPage');
    const _navigate = useNavigate();
    const [agenticWorkflows, setAgenticWorkflows] = useState<any[]>([]);
    /* Suchfeld über der Tabelle: Teilzeichenkette im Namen, siehe utilities/namensfilter.ts. */
    const [workflowFilter, setWorkflowFilter] = useState('');
    const gefilterteAgenticWorkflows = useMemo(
        () => nachNamenFiltern(agenticWorkflows, workflowFilter),
        [agenticWorkflows, workflowFilter]
    );
    const [selectedAgenticWorkflow, setSelectedAgenticWorkflow] = useState<any>();
    // Starts true: the first frame shows the table skeleton, not an empty-state flash.
    const [loadingAgenticWorkflows, setLoadingAgenticWorkflows] = useState(true);
    const [linkDialogOpen, setLinkDialogOpen] = useState<boolean>(false);
    const [linkUrl, setLinkUrl] = useState<string>('');
    const _isUpdating = useRef(false);
    const initialKey = searchParams.get('key') || 'workflow'; // Default to Workflow tab
    const [selectedKey, setSelectedKey] = useState<any>(initialKey);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // Aktiver Reiter der Ein-/Ausgabe-Schemata. Beide Reiter bleiben montiert
    // (der abgelöste `Pivot` tat das über `alwaysRender`), weil das Speichern
    // die ZodEditor-Refs beider Schemata liest; der inaktive wird nur versteckt.
    const [inputOutputTab, setInputOutputTab] = useState<string>('input');

    // Refs for ZodEditor components
    const inputZodEditorRef = useRef<any>(null);
    const outputZodEditorRef = useRef<any>(null);

    const [connectorConfigValues, setConnectorConfigValues] = useState<Record<string, string>>({});
    const [connectorConfigLoading, setConnectorConfigLoading] = useState(false);
    const [connectorConfigWorkflowId, setConnectorConfigWorkflowId] = useState<string | null>(null);
    const handleConnectorConfigChange = useCallback((nextValuesOrUpdater: React.SetStateAction<Record<string, string>>) => {
        setConnectorConfigValues((previousValues) =>
            typeof nextValuesOrUpdater === 'function'
                ? nextValuesOrUpdater(previousValues)
                : nextValuesOrUpdater
        );
    }, []);

    // Trigger detection is split so each configuration section can be shown
    // independently while still deriving shared Graph credential visibility.
    const hasGraphMailboxTrigger = useCallback((workflow: any): boolean => {
        const xml = workflow?.bpmnXML;
        if (!xml || typeof xml !== 'string') return false;
        return /\btri:connectorType\s*=\s*["']graph-mailbox["']/i.test(xml);
    }, []);

    const hasSharepointFolderTrigger = useCallback((workflow: any): boolean => {
        const xml = workflow?.bpmnXML;
        if (!xml || typeof xml !== 'string') return false;
        return /\btri:connectorType\s*=\s*["']sharepoint-folder["']/i.test(xml);
    }, []);

    const hasAIListenerConnector = useCallback((workflow: any): boolean => {
        const xml = workflow?.bpmnXML;
        if (!xml || typeof xml !== 'string') return false;
        return /<[^>]*\btri:connectorType\s*=\s*["']ai-listener["'][^>]*\/?>/i.test(xml);
    }, []);

    const _onRenderItemStyles = {
        root: { padding: '10px' },
    };

    const deleteAgenticWorkflow = (agenticWorkflow: any) => {
        try {
            showDeleteDialog(`${t('ui.deleteAgenticWorkflow')} ${agenticWorkflow.name}`,
                agenticWorkflow.name,
                async () => {
                    await userAPI.deleteMarketplaceOffer(authentication.customer, agenticWorkflow);
                    setAgenticWorkflows(agenticWorkflows.filter((aw: any) => aw._id !== agenticWorkflow._id));
                });
        } catch (error) {
            pushError(error);
        }
    };

    const agenticWorkflowColumns: ColumnDef<any, any>[] = [{
        id: 'name',
        size: 340,
        header: ({ column }) => <SortableHeader column={column}>{t('ui.name')}</SortableHeader>,
        accessorFn: (agenticWorkflow: any) => agenticWorkflow.name,
        cell: ({ row }) => <MarketplaceOfferNameCell offer={row.original} />,
    }, {
        id: 'published',
        size: 160,
        header: ({ column }) => <SortableHeader column={column}>{t('ui.internallyAvailable')}</SortableHeader>,
        accessorFn: (agenticWorkflow: any) => agenticWorkflow.published,
        cell: ({ row }) => <YesNoBadge active={Boolean(row.original.published)} />,
    }, {
        id: 'offered',
        size: 180,
        header: ({ column }) => <SortableHeader column={column}>{t('ui.offeredInMarketplace')}</SortableHeader>,
        accessorFn: (agenticWorkflow: any) => agenticWorkflow.offered,
        cell: ({ row }) => <YesNoBadge active={Boolean(row.original.offered)} />,
    }, {
        id: 'description',
        size: 400,
        header: plainHeader(t('ui.description')),
        cell: ({ row }) => <div className="line-clamp-2 break-words text-muted-foreground">{truncate(row.original.synopsis, { length: 200, separator: /,? +/ })}</div>
    }, {
        id: 'rowMenu',
        size: 56,
        header: () => <span className="sr-only">{t('ui.actions', 'Aktionen')}</span>,
        meta: rowMenuColumnMeta,
        cell: ({ row }) => (
            <OfferRowMenu
                onEdit={async () => await updateSelectedAgenticWorkflow(row.original)}
                onTest={() => openTestDialog(row.original)}
                onDelete={() => deleteAgenticWorkflow(row.original)}
            />
        ),
    }];

    const _linkColumns: ColumnDef<any, any>[] = [{
        id: 'actions',
        header: '',
        size: 80,
        // Ungenutzt: der zugehörige Link-Dialog wird nirgends geöffnet.
        cell: ({ row }) => {
            const link = row.original;
            return <div className='flex grow justify-center'>
                <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    aria-label={t('ui.delete')}
                    onClick={() => {
                        const updatedLinks = selectedAgenticWorkflow.links.filter((l: any) => l._id !== link._id);
                        updateSelectedAgenticWorkflowWithChanges({ ...selectedAgenticWorkflow, links: updatedLinks });
                    }}
                >
                    <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
            </div>;
        }
    }, {
        id: "url",
        header: plainHeader(t('ui.url')),
        accessorFn: (row: any) => row.url,
        size: 400,
        cell: ({ row }) => <div className="break-all">{row.original.url}</div>
    }, {
        id: "description",
        header: plainHeader(t('ui.description')),
        accessorFn: (row: any) => row.description,
        size: 300,
        cell: ({ row }) => <div className="break-words">{row.original.description}</div>
    }];

    const loadAgenticWorkflows = async () => {
        try {
            setLoadingAgenticWorkflows(true);
            
            const marketplaceOffers = await userAPI.getMarketplaceOffers(authentication.customer);
            const agenticWorkflows = marketplaceOffers.filter((offer: any) => offer._type === 'AgenticWorkflow');

            console.log('🔍 Loaded agentic workflows. Count:', agenticWorkflows.length);
            if (agenticWorkflows.length > 0) {
                console.log('🔍 First workflow has bpmnXML:', !!agenticWorkflows[0].bpmnXML);
            }

            setAgenticWorkflows(agenticWorkflows);
        } catch (error) {
            pushError(error);
        } finally {
            setLoadingAgenticWorkflows(false);
        }
    };

    const updateSelectedAgenticWorkflow = useCallback(async (agenticWorkflow: any) => {
        if (agenticWorkflow) {
            let fullWorkflow = agenticWorkflow;
            // If it's an existing workflow and we don't have the bpmnXML, try to refresh it
            if (agenticWorkflow._id && !agenticWorkflow.bpmnXML) {
                try {
                    console.log('🔄 Refreshing workflow to get full details (including bpmnXML)... ID:', agenticWorkflow._id, 'Customer:', authentication.customer);
                    const refreshed = await userAPI.refreshMarketplaceOffer(authentication.customer, agenticWorkflow) as any;
                    if (refreshed) {
                        console.log('✅ Workflow refreshed. bpmnXML present:', !!refreshed.bpmnXML, 'Length:', refreshed.bpmnXML?.length || 0);
                        fullWorkflow = refreshed;
                    } else {
                        console.warn('⚠️ Refresh returned null or failed for workflow ID:', agenticWorkflow._id);
                    }
                } catch (error) {
                    console.error('❌ Failed to refresh workflow:', error);
                }
            } else if (agenticWorkflow.bpmnXML) {
                console.log('ℹ️ bpmnXML already present in workflow object. Length:', agenticWorkflow.bpmnXML.length);
            }

            const newAgenticWorkflow = new AgenticWorkflow(fullWorkflow);
            // Ensure all custom fields (like bpmnXML) are preserved
            Object.assign(newAgenticWorkflow, fullWorkflow);
            console.log('✅ Setting selectedAgenticWorkflow state. ID:', newAgenticWorkflow._id, 'bpmnXML length:', (newAgenticWorkflow as any).bpmnXML?.length || 0);
            setSelectedAgenticWorkflow(newAgenticWorkflow);
        } else {
            setSelectedAgenticWorkflow(null);
        }

        if (agenticWorkflow && agenticWorkflow._id) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('agenticWorkflowId', agenticWorkflow._id);
            
            // Only preserve nodeId and key=workflow if this is internal workflow navigation
            // (not when user is navigating from workflow to external tool settings)
            const nodeId = searchParams.get('nodeId');
            const currentTab = searchParams.get('tab');
            
            // If we're staying within the workflow system (tab=4) and have nodeId, preserve context
            if (nodeId && currentTab === '4') {
                newParams.set('nodeId', nodeId);
                newParams.set('key', 'workflow');
            }
            
            setSearchParams(newParams, { replace: true });
        } else {
            // When closing (agenticWorkflow is null), clean up all workflow-related parameters
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('agenticWorkflowId');
            newParams.delete('key'); // Remove key parameter to go back to main view
            newParams.delete('nodeId'); // Remove nodeId parameter as well
            setSearchParams(newParams, { replace: true });
        }
        
        // Reset unsaved changes when selecting a new workflow
        setHasUnsavedChanges(false);
    }, [searchParams, setSearchParams, selectedKey]);

    // Wrapper function for updates that should mark as unsaved
    const updateSelectedAgenticWorkflowWithChanges = useCallback((agenticWorkflow: any) => {
        if (agenticWorkflow) {
            const newAgenticWorkflow = new AgenticWorkflow(agenticWorkflow);
            // Ensure all custom fields (like bpmnXML) are preserved
            Object.assign(newAgenticWorkflow, agenticWorkflow);
            setSelectedAgenticWorkflow(newAgenticWorkflow);
            console.log('🔍 Setting hasUnsavedChanges to true');
            setHasUnsavedChanges(true);
        }
    }, []);

    const createNewAgenticWorkflow = useCallback(() => {
        const newAgenticWorkflow = new AgenticWorkflow({
            name: '',
            description: '',
            synopsis: '',
            sampleRequests: [],
            links: [],
            published: false,
            _type: 'AgenticWorkflow'
        });

        setSelectedAgenticWorkflow(newAgenticWorkflow);
        setSelectedKey('general');
        const newParams = new URLSearchParams(searchParams);
        newParams.set('key', 'general');
        setSearchParams(newParams, { replace: true });
        setHasUnsavedChanges(false);
    }, [searchParams, setSearchParams]);

    const hasGraphMailboxConfigSection = hasGraphMailboxTrigger(selectedAgenticWorkflow);
    const hasSharepointFolderConfigSection = hasSharepointFolderTrigger(selectedAgenticWorkflow);
    const hasGraphConfigurationSection = hasGraphMailboxConfigSection || hasSharepointFolderConfigSection;
    const hasAIListenerConfigurationSection = hasAIListenerConnector(selectedAgenticWorkflow);
    const hasConfigurationTab = hasGraphConfigurationSection || hasAIListenerConfigurationSection;
    const activeConnectorConfigSchema = useMemo<ConfigurationParameter[]>(() => {
        const merged: ConfigurationParameter[] = [];
        if (hasGraphConfigurationSection) merged.push(...GRAPH_CONNECTOR_SCHEMA);
        if (hasGraphMailboxConfigSection) merged.push(...GRAPH_MAILBOX_RESOURCE_SCHEMA);
        if (hasSharepointFolderConfigSection) merged.push(...SHAREPOINT_FOLDER_RESOURCE_SCHEMA);
        if (hasAIListenerConfigurationSection) merged.push(...AI_LISTENER_CONNECTOR_SCHEMA);
        return merged;
    }, [hasGraphConfigurationSection, hasGraphMailboxConfigSection, hasSharepointFolderConfigSection, hasAIListenerConfigurationSection]);
    const selectedWorkflowId = selectedAgenticWorkflow?._id ?? null;
    const visibleConnectorConfigValues = connectorConfigWorkflowId === selectedWorkflowId ? connectorConfigValues : {};

    // Persist the per-tenant tool configuration (Azure AD creds, mailbox/folder targets,
    // AI-listener creds) if the Konfiguration tab has been loaded for the current workflow.
    // No-op when the tab was never visited (we don't want to overwrite a real ToolConfiguration
    // with an empty payload).
    // Connector save errors are surfaced to the user but should not prevent the workflow save
    // flow from refreshing/closing the editor.
    const persistConnectorConfigurationIfNeeded = useCallback(async () => {
        if (!selectedWorkflowId || !authentication?.customer) return;
        if (!hasConfigurationTab) return;
        if (connectorConfigWorkflowId !== selectedWorkflowId) return;
        try {
            await userAPI.putToolConfiguration(
                authentication.customer,
                selectedWorkflowId,
                connectorConfigValues,
                activeConnectorConfigSchema,
            );
        } catch (error) {
            pushError(error);
        }
    }, [
        selectedWorkflowId,
        authentication?.customer,
        hasConfigurationTab,
        connectorConfigWorkflowId,
        connectorConfigValues,
        activeConnectorConfigSchema,
        pushError,
    ]);

    // Save function for the workflow
    const _saveWorkflow = useCallback(async () => {
        if (!selectedAgenticWorkflow) return;

        // Get current schema from ZodEditor refs
        let updatedAgenticWorkflow = new AgenticWorkflow(selectedAgenticWorkflow);
        // Ensure all custom fields (like bpmnXML) are preserved
        Object.assign(updatedAgenticWorkflow, selectedAgenticWorkflow);

        // Ensure graph is included from selectedAgenticWorkflow
        if (selectedAgenticWorkflow.graph) {
            (updatedAgenticWorkflow as any).graph = selectedAgenticWorkflow.graph;
        }

        if (inputZodEditorRef.current) {
            const inputSchema = inputZodEditorRef.current.getCurrentSchema();
            updatedAgenticWorkflow.setInputSchemaFields(inputSchema.fields);
        }

        if (outputZodEditorRef.current) {
            const outputSchema = outputZodEditorRef.current.getCurrentSchema();
            updatedAgenticWorkflow.setOutputSchemaFields(outputSchema.fields);
        }

        const savedOffer = await userAPI.updateMarketplaceOffer(authentication.customer, updatedAgenticWorkflow);
        // Persist the per-tenant tool configuration alongside the workflow so a single
        // Save action covers both Workflow and Konfiguration tabs.
        await persistConnectorConfigurationIfNeeded();
        await loadAgenticWorkflows();
        setHasUnsavedChanges(false);

        // Update the current selected workflow with the saved version
        // Prefer using the returned object from update if available, as it's guaranteed to have all fields
        if (savedOffer) {
            await updateSelectedAgenticWorkflow(savedOffer);
        } else {
            // Fallback to finding it in the refreshed list
            const marketplaceOffers = await userAPI.getMarketplaceOffers(authentication.customer);
            const foundWorkflow = marketplaceOffers.find((w: any) => w._id === updatedAgenticWorkflow._id);
            if (foundWorkflow) {
                await updateSelectedAgenticWorkflow(foundWorkflow);
            }
        }
    }, [selectedAgenticWorkflow, authentication.customer, persistConnectorConfigurationIfNeeded]);

    // Save and close function for the workflow
    const saveAndCloseWorkflow = useCallback(async () => {
        if (!selectedAgenticWorkflow) return;

        // Get current schema from ZodEditor refs
        let updatedAgenticWorkflow = new AgenticWorkflow(selectedAgenticWorkflow);
        // Ensure all custom fields (like bpmnXML) are preserved
        Object.assign(updatedAgenticWorkflow, selectedAgenticWorkflow);

        // Ensure graph is included from selectedAgenticWorkflow
        if (selectedAgenticWorkflow.graph) {
            (updatedAgenticWorkflow as any).graph = selectedAgenticWorkflow.graph;
        }

        if (inputZodEditorRef.current) {
            const inputSchema = inputZodEditorRef.current.getCurrentSchema();
            updatedAgenticWorkflow.setInputSchemaFields(inputSchema.fields);
        }

        if (outputZodEditorRef.current) {
            const outputSchema = outputZodEditorRef.current.getCurrentSchema();
            updatedAgenticWorkflow.setOutputSchemaFields(outputSchema.fields);
        }

        await userAPI.updateMarketplaceOffer(authentication.customer, updatedAgenticWorkflow);
        await persistConnectorConfigurationIfNeeded();
        await loadAgenticWorkflows();

        // Close the workflow by setting it to null (triggers URL cleanup)
        await updateSelectedAgenticWorkflow(null);
        setHasUnsavedChanges(false);
    }, [selectedAgenticWorkflow, authentication.customer, persistConnectorConfigurationIfNeeded]);

    const menuItems = withMarketplaceMenuItems([
        {
            key: 'general',
            icon: 'Global',
            name: t('ui.generalInformation'),
            title: 'Add',
            onClick: () => {
                setSelectedKey('general');
                updateURLFromSelectedKey('general');
            },
        },
        {
            key: 'offerStatus',
            icon: 'StatusCircleCheckmark',
            name: t('ui.offerStatus'),
            title: t('ui.offerStatus'),
            onClick: () => {
                setSelectedKey('offerStatus');
                updateURLFromSelectedKey('offerStatus');
            },
        },
        {
            key: 'workflow',
            icon: 'Flow',
            name: t('ui.workflow'),
            title: t('ui.workflow'),
            onClick: () => {
                setSelectedKey('workflow');
                updateURLFromSelectedKey('workflow');
            },
        },
        {
            key: 'inputOutput',
            icon: 'DataConnectionLibrary',
            name: t('ui.inputOutputData'),
            title: t('ui.inputOutputData'),
            onClick: () => {
                setSelectedKey('inputOutput');
                updateURLFromSelectedKey('inputOutput');
            },
        },
        {
            key: 'sampleRequests',
            icon: 'SampleRequests',
            name: t('ui.sampleRequests'),
            title: t('ui.sampleRequests'),
            onClick: () => {
                setSelectedKey('sampleRequests');
                updateURLFromSelectedKey('sampleRequests');
            },
        },
        {
            key: 'details',
            icon: 'Details',
            name: t('ui.offerDetails'),
            title: t('ui.offerDetails'),
            onClick: () => {
                setSelectedKey('details');
                updateURLFromSelectedKey('details');
            },
        },
        {
            key: 'billing',
            icon: 'Billing',
            name: t('ui.billing'),
            title: t('ui.billing'),
            onClick: () => {
                setSelectedKey('billing');
                updateURLFromSelectedKey('billing');
            },
        },
        ...(hasConfigurationTab ? [{
            key: 'configuration',
            icon: 'Settings',
            name: t('ui.configuration'),
            title: t('ui.configuration'),
            onClick: () => {
                setSelectedKey('configuration');
                updateURLFromSelectedKey('configuration');
            },
        }] : []),
    ], authentication?.customerObject);

    // Get valid menu keys for this tool type
    const validMenuKeys = menuItems.map(item => item.key);

    // Validate and fix selectedKey if it's not valid for this tool type
    const validateAndFixSelectedKey = useCallback(() => {
        if (!validMenuKeys.includes(selectedKey)) {
            setSelectedKey('workflow'); // Fallback to Workflow tab
            
            // Update URL to reflect the fallback
            const newParams = new URLSearchParams(searchParams);
            newParams.set('key', 'workflow');
            setSearchParams(newParams, { replace: true });
            return true; // Indicates we made a change
        }
        return false; // No change needed
    }, [validMenuKeys, selectedKey, searchParams, setSearchParams]);

    useEffect(() => {
        // Revalidate selectedKey when dynamic tabs change (e.g. configuration tab disappears after BPMN edits).
        // This keeps the UI on a valid tab and avoids rendering unknown-menu fallbacks.
        validateAndFixSelectedKey();
    }, [selectedKey, hasConfigurationTab, validateAndFixSelectedKey]);

    const onImagesChange = async (imageList: any, _addUpdateIndex: any) => {
        try {
            const form = new FormData();
            form.append("file", imageList[0].file);
            const image = await userAPI.uploadFile(form);
            updateSelectedAgenticWorkflowWithChanges({ ...selectedAgenticWorkflow, images: [...(selectedAgenticWorkflow.images || []), image.path] });
        } catch (error: any) {
            pushError(error);
        }
    };

    useEffect(() => {
        loadAgenticWorkflows();
        
    }, [authentication.customer]);

    useEffect(() => {
        // Load agentic workflow from URL if agenticWorkflowId is present
        const agenticWorkflowId = searchParams.get('agenticWorkflowId');
        if (agenticWorkflowId && agenticWorkflowId !== 'new' && agenticWorkflows.length > 0) {
            const agenticWorkflow = agenticWorkflows.find(aw => aw._id === agenticWorkflowId);
            if (agenticWorkflow && (!selectedAgenticWorkflow || selectedAgenticWorkflow._id !== agenticWorkflow._id)) {
                updateSelectedAgenticWorkflow(agenticWorkflow);
                
            }
        }
    }, [searchParams, agenticWorkflows, selectedAgenticWorkflow, updateSelectedAgenticWorkflow]);

    useEffect(() => {
        const keyParam = searchParams.get('key') || 'workflow'; // Default to Workflow tab
        const nodeId = searchParams.get('nodeId');
        
        // Special handling: if nodeId is present, it means "used in" navigation
        // In this case, we should always be on the workflow tab
        if (nodeId && keyParam === 'workflow') {
            if (selectedKey !== 'workflow') {
                setSelectedKey('workflow');
            }
        } else if (keyParam !== selectedKey) {
            // Check if the key is valid for agentic workflows before setting it
            if (validMenuKeys.includes(keyParam)) {
                setSelectedKey(keyParam);
            } else {
                // Fallback to workflow for invalid keys
                setSelectedKey('workflow');
                
                // Update URL to reflect the fallback (but only if not already workflow)
                if (keyParam !== 'workflow') {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('key', 'workflow');
                    setSearchParams(newParams, { replace: true });
                }
            }
        }
    }, [searchParams.get('key'), searchParams.get('nodeId')]); // Only depend on specific parameters

    useEffect(() => {
        if (selectedWorkflowId && hasConfigurationTab && authentication?.customer) {
            return;
        }

        setConnectorConfigLoading(false);
    }, [selectedWorkflowId, hasConfigurationTab, authentication?.customer]);

    useEffect(() => {
        // Hard-reset values only when there is no workflow selected anymore.
        // This prevents a short empty-state flash while configuration reloads for an existing workflow context.
        if (!selectedWorkflowId) {
            setConnectorConfigValues({});
            setConnectorConfigWorkflowId(null);
        }
    }, [selectedWorkflowId]);

    useEffect(() => {
        let isCurrentRequest = true;

        const loadConnectorConfiguration = async () => {
            if (selectedKey !== 'configuration') {
                setConnectorConfigLoading(false);
                return;
            }

            if (!hasConfigurationTab || !selectedWorkflowId || !authentication?.customer) {
                setConnectorConfigLoading(false);
                return;
            }

            setConnectorConfigLoading(true);

            try {
                const config = await userAPI.getToolConfiguration(authentication.customer, selectedWorkflowId);
                if (!isCurrentRequest) return;
                setConnectorConfigValues(config?.schemaConfiguration ?? {});
                setConnectorConfigWorkflowId(selectedWorkflowId);
            } catch (error) {
                if (!isCurrentRequest) return;
                setConnectorConfigValues({});
                setConnectorConfigWorkflowId(selectedWorkflowId);
            } finally {
                if (!isCurrentRequest) return;
                setConnectorConfigLoading(false);
            }
        };

        loadConnectorConfiguration();

        return () => {
            isCurrentRequest = false;
        };
    }, [selectedKey, hasConfigurationTab, selectedWorkflowId, authentication?.customer]);

    // Simplified key sync - only update URL when user manually changes tabs (not from URL changes)
    const updateURLFromSelectedKey = (newKey: string) => {
        const currentTab = searchParams.get('tab');
        
        // Only update URL if we're still within the workflow system (tab=4)
        if (currentTab === '4') {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('key', newKey);
            
            // Preserve nodeId parameter if it exists
            const nodeId = searchParams.get('nodeId');
            if (nodeId) {
                newParams.set('nodeId', nodeId);
            }
            
            setSearchParams(newParams, { replace: true });
        }
    };

    return <><div className="w-full">
        {selectedAgenticWorkflow
            ?
            <div className="w-full rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-0">
                        <div className="text-sm">{t('ui.agenticWorkflow')}</div>
                        <div className="text-base font-bold">{selectedAgenticWorkflow.name}</div>
                    </div>
                    <div className="flex grow flex-wrap items-center justify-end gap-4">
                        {hasUnsavedChanges && (
                            <Badge variant="warning" className="gap-1 whitespace-nowrap">
                                <AlertTriangle className="h-3 w-3" aria-hidden />
                                {t('global.unsavedChanges')}
                            </Badge>
                        )}
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={t('ui.test')}
                                        onClick={() => openTestDialog(selectedAgenticWorkflow)}
                                    >
                                        <Rocket className="h-4 w-4" aria-hidden />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.test')}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <Button variant="outline" onClick={async () => {
                            console.log('🔍 Cancel/Close button clicked, hasUnsavedChanges:', hasUnsavedChanges);
                            await updateSelectedAgenticWorkflow(null);
                        }}>{hasUnsavedChanges ? t('ui.cancel') : t('global.close')}</Button>
                        {selectedAgenticWorkflow._id && selectedAgenticWorkflow.lastStatus !== OfferStatus.discontinued ?
                            <Button onClick={async () => {
                                // Delegate to the shared save-and-close callback so this single
                                // Save button persists BOTH the AgenticWorkflow (BPMN, schemas,
                                // …) AND the per-tenant ToolConfiguration (Azure AD creds,
                                // mailbox/folder targets, AI listener creds). Previously this
                                // button had its own inlined save which only PUT the offer and
                                // silently dropped any Konfiguration tab edits.
                                try {
                                    await saveAndCloseWorkflow();
                                } catch (error) {
                                    pushError(error);
                                }
                            }}>{t('ui.save')}</Button>
                            :
                            <Button onClick={async () => {
                                // Get current schema from ZodEditor refs
                                let updatedAgenticWorkflow = new AgenticWorkflow(selectedAgenticWorkflow);
                                // Ensure all custom fields (like bpmnXML) are preserved
                                Object.assign(updatedAgenticWorkflow, selectedAgenticWorkflow);
                                
                                // Ensure graph is included from selectedAgenticWorkflow
                                if (selectedAgenticWorkflow.graph) {
                                    (updatedAgenticWorkflow as any).graph = selectedAgenticWorkflow.graph;
                                }
                                
                                if (inputZodEditorRef.current) {
                                    const inputSchema = inputZodEditorRef.current.getCurrentSchema();
                                    updatedAgenticWorkflow.setInputSchemaFields(inputSchema.fields);
                                }
                                
                                if (outputZodEditorRef.current) {
                                    const outputSchema = outputZodEditorRef.current.getCurrentSchema();
                                    updatedAgenticWorkflow.setOutputSchemaFields(outputSchema.fields);
                                }
                                
                                await userAPI.createMarketplaceOffer(authentication.customer, updatedAgenticWorkflow);
                                await loadAgenticWorkflows();
                                await updateSelectedAgenticWorkflow(null);
                            }}>{t('ui.create')}</Button>
                        }
                    </div>
                </div>
                <div className="mt-3 flex gap-4 rounded-md border p-3">
                    <div className="w-[200px] shrink-0">
                        {menuItems.map((item) => (
                            <button
                                type="button"
                                key={item.key}
                                className={`flex w-full items-center p-3 text-left hover:bg-accent/50 ${selectedKey === item.key ? 'bg-accent' : ''}`}
                                onClick={item.onClick}
                            >
                                <div className="text-sm">{item.name}</div>
                            </button>
                        ))}
                    </div>
                    <div className="min-w-0 flex-1">
                        {(() => {
                            switch (selectedKey) {
                                case 'general':
                                    return (
                                        <div className="w-full">
                                            <div className="flex flex-wrap gap-4">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-col gap-1.5">
                                                        <Label htmlFor="agentic-workflow-name">{t('ui.workflowName')}</Label>
                                                        <Input
                                                            id="agentic-workflow-name"
                                                            value={selectedAgenticWorkflow.name}
                                                            onChange={(e) => {
                                                                updateSelectedAgenticWorkflowWithChanges({ ...selectedAgenticWorkflow, name: e.target.value });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3 flex flex-col gap-1.5">
                                                        <Label htmlFor="agentic-workflow-description">{t('ui.workflowDescription')}</Label>
                                                        <Textarea
                                                            id="agentic-workflow-description"
                                                            value={selectedAgenticWorkflow.description}
                                                            rows={15}
                                                            className="resize-none"
                                                            onChange={(e) => {
                                                                updateSelectedAgenticWorkflowWithChanges({ ...selectedAgenticWorkflow, description: e.target.value });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3 text-sm text-primary">{t('ui.shortDescriptionUsedByAI')}</div>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap gap-4">
                                                        <FluentIconSelector
                                                            label="Icon"
                                                            selectedIcon={selectedAgenticWorkflow.icon || 'Flow'}
                                                            iconColor={selectedAgenticWorkflow.iconColor}
                                                            onIconChange={(iconName) => {
                                                                updateSelectedAgenticWorkflowWithChanges({ ...selectedAgenticWorkflow, icon: iconName });
                                                            }}
                                                            placeholder="Icon auswählen..."
                                                        />
                                                        <ColorSelector
                                                            label="Icon Color"
                                                            selectedColor={selectedAgenticWorkflow.iconColor}
                                                            onColorChange={(color) => {
                                                                updateSelectedAgenticWorkflowWithChanges({ ...selectedAgenticWorkflow, iconColor: color });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <InternallyAvailableCheckbox
                                                            marketplaceOffer={selectedAgenticWorkflow}
                                                            updateSelectedMarketplaceOffer={updateSelectedAgenticWorkflowWithChanges}
                                                        />
                                                    </div>
                                                    
                                                    {selectedAgenticWorkflow._id && (
                                                        <div className="mt-3">
                                                            <ToolUsageList
                                                                toolId={selectedAgenticWorkflow._id}
                                                                hasUnsavedChanges={hasUnsavedChanges}
                                                                onSave={saveAndCloseWorkflow}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                case 'offerStatus':
                                    return (
                                        <OfferStatusManagement
                                            marketplaceOffer={selectedAgenticWorkflow}
                                            updateSelectedMarketplaceOffer={updateSelectedAgenticWorkflowWithChanges}
                                        />
                                    );
                                case 'workflow':
                                    console.log('🏗️ Rendering BpmnWorkflowEditor. XML present:', !!selectedAgenticWorkflow.bpmnXML);
                                    return (
                                        <BpmnWorkflowEditor
                                            key={`bpmn-${selectedAgenticWorkflow._id}`}
                                            initialXml={selectedAgenticWorkflow.bpmnXML}
                                            onChanged={(xml) => {
                                                console.log('🔍 BpmnWorkflowEditor onChanged called');
                                                updateSelectedAgenticWorkflowWithChanges({
                                                    ...selectedAgenticWorkflow,
                                                    bpmnXML: xml,
                                                });
                                            }}
                                        />
                                    );
                                case 'inputOutput':
                                    return (
                                        <div className="w-full max-w-[1000px]">
                                            <Tabs
                                                className="mt-6"
                                                value={inputOutputTab}
                                                onValueChange={setInputOutputTab}
                                            >
                                                <TabsList>
                                                    <TabsTrigger value="input">Eingabe</TabsTrigger>
                                                    <TabsTrigger value="output">Ausgabe</TabsTrigger>
                                                </TabsList>
                                                <TabsContent value="input" forceMount hidden={inputOutputTab !== 'input'}>
                                                    <div className="mt-5">
                                                        <ZodEditor
                                                            key={`input-${selectedAgenticWorkflow._id}`}
                                                            ref={inputZodEditorRef}
                                                            value={selectedAgenticWorkflow.getInputSchemaString()}
                                                            initialFields={getInputSchemaFields(selectedAgenticWorkflow)}
                                                            onChange={() => setHasUnsavedChanges(true)}
                                                            placeholder="Ziehe Feldtypen hierher, um das Eingabeschema zu definieren..."
                                                            debugName="INPUT"
                                                        />
                                                    </div>
                                                </TabsContent>
                                                <TabsContent value="output" forceMount hidden={inputOutputTab !== 'output'}>
                                                    <div className="mt-5">
                                                        <ZodEditor
                                                            key={`output-${selectedAgenticWorkflow._id}`}
                                                            ref={outputZodEditorRef}
                                                            value={selectedAgenticWorkflow.getOutputSchemaString()}
                                                            initialFields={getOutputSchemaFields(selectedAgenticWorkflow)}
                                                            onChange={() => setHasUnsavedChanges(true)}
                                                            placeholder="Ziehe Feldtypen hierher, um das Ausgabeschema zu definieren..."
                                                            debugName="OUTPUT"
                                                        />
                                                    </div>
                                                </TabsContent>
                                            </Tabs>
                                            <div className="mt-3 text-sm text-primary">Diese Zod Schemas helfen der AI zu verstehen, welche Daten der Workflow erwartet und zurückgibt.</div>
                                        </div>
                                    );
                                case 'sampleRequests':
                                    return (
                                        <SampleRequests
                                            type="AgenticWorkflow"
                                            marketplaceOffer={selectedAgenticWorkflow as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedAgenticWorkflowWithChanges}
                                        />
                                    );
                                case 'details':
                                    return (
                                        <MarketingDetails
                                            type="AgenticWorkflow"
                                            marketplaceOffer={selectedAgenticWorkflow as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedAgenticWorkflowWithChanges}
                                            onImagesChange={onImagesChange}
                                        />
                                    );
                                case 'billing':
                                    return (
                                        <PlanPanel
                                            type={AgenticWorkflow.type}
                                            marketplaceOffer={selectedAgenticWorkflow as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedAgenticWorkflowWithChanges}
                                        />
                                    );
                                case 'configuration':
                                    return (
                                        <div className="max-w-[520px]">
                                            {hasGraphConfigurationSection && (
                                                <>
                                                    <div className="mb-4 text-xs font-bold">{t('workflowEditor.connector.graphApiCredentials')}</div>
                                                    <div className="mb-4 text-sm text-muted-foreground">
                                                        {t('workflowEditor.connector.graphApiDescription')}
                                                    </div>
                                                    <ToolConfigurationForm
                                                        schema={GRAPH_CONNECTOR_SCHEMA}
                                                        value={visibleConnectorConfigValues}
                                                        disabled={connectorConfigLoading}
                                                        onChange={handleConnectorConfigChange}
                                                    />
                                                </>
                                            )}
                                            {hasGraphMailboxConfigSection && (
                                                <div className={hasGraphConfigurationSection ? 'mt-6' : ''}>
                                                    <div className="mb-4 text-xs font-bold">{t('workflowEditor.connector.mailboxResource')}</div>
                                                    <div className="mb-4 text-sm text-muted-foreground">
                                                        {t('workflowEditor.connector.mailboxResourceDescription')}
                                                    </div>
                                                    <ToolConfigurationForm
                                                        schema={GRAPH_MAILBOX_RESOURCE_SCHEMA}
                                                        value={visibleConnectorConfigValues}
                                                        disabled={connectorConfigLoading}
                                                        onChange={handleConnectorConfigChange}
                                                    />
                                                </div>
                                            )}
                                            {hasSharepointFolderConfigSection && (
                                                <div className={(hasGraphConfigurationSection || hasGraphMailboxConfigSection) ? 'mt-6' : ''}>
                                                    <div className="mb-4 text-xs font-bold">{t('workflowEditor.connector.sharepointFolderResource')}</div>
                                                    <div className="mb-4 text-sm text-muted-foreground">
                                                        {t('workflowEditor.connector.sharepointFolderResourceDescription')}
                                                    </div>
                                                    <ToolConfigurationForm
                                                        schema={SHAREPOINT_FOLDER_RESOURCE_SCHEMA}
                                                        value={visibleConnectorConfigValues}
                                                        disabled={connectorConfigLoading}
                                                        onChange={handleConnectorConfigChange}
                                                    />
                                                </div>
                                            )}
                                            {hasAIListenerConfigurationSection && (
                                                <div className={(hasGraphConfigurationSection || hasGraphMailboxConfigSection || hasSharepointFolderConfigSection) ? 'mt-6' : ''}>
                                                    <div className="mb-4 text-xs font-bold">{t('workflowEditor.connector.aiListenerCredentials')}</div>
                                                    <div className="mb-4 text-sm text-muted-foreground">
                                                        {t('workflowEditor.connector.aiListenerDescription')}
                                                    </div>
                                                    <ToolConfigurationForm
                                                        schema={AI_LISTENER_CONNECTOR_SCHEMA}
                                                        value={visibleConnectorConfigValues}
                                                        disabled={connectorConfigLoading}
                                                        onChange={handleConnectorConfigChange}
                                                    />
                                                </div>
                                            )}
                                            {/* Save is unified at the workflow level. The workflow Save button
                                                also persists this Konfiguration tab so users don't have to remember
                                                two separate save actions. */}
                                        </div>
                                    );
                                default:
                                    return <div>{t('ui.unknownMenuItem')}</div>;
                            }
                        })()}
                    </div>
                </div>
            </div>
            :
            <div className='mt-5'>
                <div className="flex flex-wrap items-center gap-3">
                    <NameFilterBar
                        wert={workflowFilter}
                        onChange={setWorkflowFilter}
                        seite={paginationProps.page}
                        onSeiteChange={paginationProps.onPageChange}
                        anzahl={gefilterteAgenticWorkflows.length}
                        gesamt={(agenticWorkflows ?? []).length}
                    />
                    <Button variant="ghost" className="gap-2" onClick={createNewAgenticWorkflow}>
                        <Plus className="h-4 w-4" aria-hidden />
                        Agentischen Workflow hinzufügen
                    </Button>
                </div>

                <div className='mt-5 overflow-x-auto'>
                    <DataTable
                        columns={agenticWorkflowColumns}
                        data={gefilterteAgenticWorkflows}
                        {...paginationProps}
                        loading={loadingAgenticWorkflows && agenticWorkflows.length === 0}
                        onRowClick={(workflow: any) => { void updateSelectedAgenticWorkflow(workflow); }}
                        emptyMessage={<div className='p-6'>Keine Agentischen Workflows gefunden</div>}
                    />
                </div>
            </div>
        }

        <Dialog
            open={linkDialogOpen}
            onOpenChange={(open) => { if (!open) setLinkDialogOpen(false); }}
        >
            <DialogContent className="max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Link hinzufügen</DialogTitle>
                </DialogHeader>
                <div className="mt-5 flex flex-col gap-3">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="agentic-workflow-link-url">
                            URL<span className="ml-0.5 text-destructive">*</span>
                        </Label>
                        <Input
                            id="agentic-workflow-link-url"
                            required
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="agentic-workflow-link-description">Beschreibung</Label>
                        <Textarea
                            id="agentic-workflow-link-description"
                            rows={3}
                            className="resize-none"
                            onChange={(e) => setLinkUrl(e.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={() => {
                        const newLink = {
                            _id: Date.now().toString(),
                            url: linkUrl,
                            description: linkUrl
                        };
                        updateSelectedAgenticWorkflowWithChanges({
                            ...selectedAgenticWorkflow,
                            links: [...(selectedAgenticWorkflow.links || []), newLink]
                        });
                        setLinkUrl('');
                        setLinkDialogOpen(false);
                    }}>Hinzufügen</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {TestDialog}
    </div></>;
}
